import React, { useEffect, useState, useCallback } from 'react';
import './RecallModal.css';
import { ticketLabel } from '../utils/serviceCode';

/**
 * Modale pour afficher le rappel ou l'appel d'un ticket
 * Design moderne et attractif avec animations.
 *
 * SEUL popup de notification de l'écran /display (appel ET rappel via `isRecall`).
 * Entièrement configurable depuis la console admin via `settings` (= config.display.recall) :
 * couleurs (dégradé de fond, accent, guichet, texte), opacité du voile, largeur max,
 * échelle des chiffres, durée d'affichage et disposition (horizontal/vertical/compact).
 * Les réglages alimentent des variables CSS ; toute valeur absente retombe sur le design d'origine.
 *
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */
export const RecallModal = ({ ticket, counterId, counterName, isRecall = true, onClose, duration = 5000, settings = {}, config = null, lang = 'fr' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  // Durée effective : la config (durationSec) prime sur la prop `duration` (repli 5 s).
  const effectiveDuration = Number(settings.durationSec) > 0
    ? Number(settings.durationSec) * 1000
    : (duration || 5000);

  // Orientation du popup (classe CSS) : 'horizontal' (défaut) | 'vertical' | 'compact'.
  const orientation = ['vertical', 'compact'].includes(settings.orientation) ? settings.orientation : 'horizontal';

  // Réglages → variables CSS + largeur/échelle. Chaque `undefined` laisse la CSS
  // retomber sur son fallback (le design violet d'origine).
  const styleVars = {};
  if (settings.bgStart) styleVars['--recall-bg-start'] = settings.bgStart;
  if (settings.bgEnd) styleVars['--recall-bg-end'] = settings.bgEnd;
  if (settings.accent) styleVars['--recall-accent'] = settings.accent;
  if (settings.counterColor) styleVars['--recall-counter'] = settings.counterColor;
  if (settings.textColor) styleVars['--recall-text'] = settings.textColor;
  if (settings.overlayOpacity != null && settings.overlayOpacity !== '') {
    styleVars['--recall-overlay'] = `rgba(0, 0, 0, ${Number(settings.overlayOpacity)})`;
  }
  if (Number(settings.maxWidth) > 0) styleVars['--recall-max-width'] = `${Number(settings.maxWidth)}px`;
  if (Number(settings.numberScale) > 0) styleVars['--recall-number-scale'] = String(Number(settings.numberScale));

  const handleClose = () => {
    console.log('🔴 Fermeture de la modale - début animation');
    setIsVisible(false);
    setTimeout(() => {
      console.log('🔴 Appel de onClose');
      if (onClose) onClose();
    }, 300);
  };

  useEffect(() => {
    console.log('🟢 Modale montée - durée:', effectiveDuration, 'ms');

    // Animer l'apparition
    const appearTimeout = setTimeout(() => {
      setIsVisible(true);
      console.log('✅ Modale visible');
    }, 10);

    // Animer la barre de progression
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - (100 / (effectiveDuration / 100));
        return newProgress <= 0 ? 0 : newProgress;
      });
    }, 100);

    // Fermer automatiquement après la durée
    const closeTimeout = setTimeout(() => {
      console.log('⏰ Timeout atteint - fermeture automatique dans', effectiveDuration, 'ms');
      setIsVisible(false);
      setTimeout(() => {
        console.log('🔴 Fermeture automatique - appel de onClose');
        if (onClose) onClose();
      }, 300);
    }, effectiveDuration);

    return () => {
      console.log('🧹 Nettoyage de la modale');
      clearTimeout(appearTimeout);
      clearInterval(interval);
      clearTimeout(closeTimeout);
    };
  }, [effectiveDuration, onClose]);

  if (!ticket) return null;

  return (
    <div className={`recall-modal-overlay ${isVisible ? 'visible' : ''}`} style={styleVars} onClick={handleClose}>
      <div className={`recall-modal recall-modal--${orientation} ${isVisible ? 'visible' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* En-tête de la modale */}
        <div className="recall-modal-header">
          <div className="recall-icon-container">
            <svg className="recall-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <h2 className="recall-title">
            <span className="recall-title-main">
              {isRecall ? 'RAPPEL DE TICKET' : 'APPEL DE TICKET'}
            </span>
            <span className="recall-title-ar">
              {isRecall ? 'استدعاء التذكرة' : 'نداء التذكرة'}
            </span>
          </h2>
          <button className="recall-close-btn" onClick={handleClose} aria-label="Fermer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Corps de la modale */}
        <div className="recall-modal-body">
          {/* Numéro du ticket */}
          <div className="recall-ticket-number">
            <span className="recall-label">Numéro de ticket</span>
            <span className="recall-label-ar">رقم التذكرة</span>
            <div className="recall-ticket-id">
              {/* Ce que le client LIT doit être ce qu'il ENTEND : même code par langue que
                  l'annonce vocale. Sans `config`, repli sur l'identifiant technique. */}
              {ticket.label || (config ? ticketLabel(config, ticket, lang) : '') || ticket.ticketId || 'N/A'}
            </div>
          </div>

          {/* Flèche animée */}
          <div className="recall-arrow">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z"/>
            </svg>
          </div>

          {/* Guichet */}
          <div className="recall-counter">
            <span className="recall-label">Présentez-vous au</span>
            <span className="recall-label-ar">تقدم إلى</span>
            <div className="recall-counter-name">
              {counterName || `Guichet ${counterId}`}
            </div>
            <div className="recall-counter-number">
              {counterId}
            </div>
          </div>
        </div>

        {/* Message de rappel */}
        <div className="recall-message">
          <p className="recall-message-text">
            Veuillez vous présenter immédiatement
          </p>
          <p className="recall-message-text-ar">
            يرجى التقدم فورا
          </p>
        </div>

        {/* Barre de progression */}
        <div className="recall-progress-bar">
          <div
            className="recall-progress-fill"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Pied de page avec compteur de rappels */}
        {isRecall && ticket.recallCount > 0 && (
          <div className="recall-footer">
            <div className="recall-count-badge">
              <span className="recall-count-icon">🔔</span>
              <span className="recall-count-text">
                Rappel #{ticket.recallCount}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecallModal;
